import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import SundaySchoolLayout from '../SundaySchoolLayout';
import {
  canManageDepartment,
  getSelectedDepartment,
  getSelectedDepartmentId,
  getSundaySchoolSession,
} from '../sundaySchoolSession';

// 부서일정 - 주일학교 앱 '부서일정' 탭에 날짜순 목록으로 보이는 일정을 부서별로 관리한다.
// 교회앱관리(/manage-app)의 교회일정 화면과 같은 월별 목록 + 등록 폼. 서버: /sundayschool/schedules.
// 등록·수정·삭제는 부서담당자 이상만(서버가 다시 확인), 교사·스텝은 보기만.

type ScheduleCategory = '예배' | '행사' | '모임' | '교육' | '기타';

const SCHEDULE_CATEGORIES: ScheduleCategory[] = ['예배', '행사', '모임', '교육', '기타'];

const CATEGORY_BADGE: Record<ScheduleCategory, string> = {
  예배: 'badge-sky',
  행사: 'badge-coral',
  모임: 'badge-ok',
  교육: 'badge-yellow',
  기타: 'badge-gray',
};

type ScheduleRow = {
  id: number;
  category: ScheduleCategory;
  title: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  place: string;
  description: string | null;
};

type Holiday = { id: number; date: string; name: string };

type ScheduleMonth = { month: string; schedules: ScheduleRow[]; holidays: Holiday[] };

type ScheduleForm = Omit<ScheduleRow, 'id' | 'description'> & { description: string };

type ScheduleDay = { date: string; holidays: Holiday[]; schedules: ScheduleRow[] };

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

const pad2 = (value: number) => String(value).padStart(2, '0');
const toYmd = (date: Date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const currentMonth = () => toYmd(new Date()).slice(0, 7);

function shiftMonth(month: string, delta: number) {
  const [y, m] = month.split('-').map(Number);
  const date = new Date(y, m - 1 + delta, 1);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;
}

function monthLabel(month: string) {
  const [y, m] = month.split('-').map(Number);
  return `${y}년 ${m}월`;
}

function weekdayIndex(ymd: string) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}

function shortDate(ymd: string) {
  const [, m, d] = ymd.split('-').map(Number);
  return `${m}.${d}(${WEEKDAYS[weekdayIndex(ymd)]})`;
}

function scheduleTimeText(row: ScheduleRow) {
  const time = row.startTime ? `${row.startTime}${row.endTime ? ` ~ ${row.endTime}` : ''}` : '';
  if (row.startDate !== row.endDate) {
    const range = `${shortDate(row.startDate)} ~ ${shortDate(row.endDate)}`;
    return time ? `${range} · ${time}` : range;
  }
  return time || '종일';
}

// 여러 날짜 일정은 시작일(이전 달에 시작했으면 그 달 1일)에 한 번만 보인다 — 앱 목록과 같은 규칙.
function groupByDay(data: ScheduleMonth): ScheduleDay[] {
  const monthStart = `${data.month}-01`;
  const days = new Map<string, ScheduleDay>();
  const dayOf = (date: string) => {
    let day = days.get(date);
    if (!day) {
      day = { date, holidays: [], schedules: [] };
      days.set(date, day);
    }
    return day;
  };
  data.holidays.forEach((holiday) => dayOf(holiday.date).holidays.push(holiday));
  data.schedules.forEach((row) => dayOf(row.startDate < monthStart ? monthStart : row.startDate).schedules.push(row));
  return Array.from(days.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function emptyForm(date: string): ScheduleForm {
  return {
    category: '행사',
    title: '',
    startDate: date,
    endDate: date,
    startTime: '',
    endTime: '',
    place: '',
    description: '',
  };
}

function errorMessageOf(err: unknown, fallback: string) {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  return (data && typeof data === 'object' && 'message' in data && String(data.message)) || fallback;
}

export default function SundaySchoolSchedule() {
  const session = getSundaySchoolSession();
  const department = getSelectedDepartment();
  const canEdit = canManageDepartment(session);
  const [month, setMonth] = useState(currentMonth);
  const [data, setData] = useState<ScheduleMonth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latestRequest = useRef('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ScheduleForm>(emptyForm(toYmd(new Date())));
  const [multiDay, setMultiDay] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLDivElement>(null);

  // 쓰기 요청에 싣는 본인·부서 정보.
  const identity = () => ({
    authChurch: session?.user.authChurch,
    departmentId: getSelectedDepartmentId(),
    userId: session?.user.id,
  });

  const fetchMonth = useCallback(
    (target: string) => {
      if (!session) return;
      latestRequest.current = target;
      setLoading(true);
      setError(null);
      axios
        .get(`${MainURL}/sundayschool/schedules`, {
          params: { authChurch: session.user.authChurch, departmentId: getSelectedDepartmentId(), month: target },
        })
        .then((res) => {
          if (latestRequest.current === target) setData(res.data?.data ?? null);
        })
        .catch((err) => {
          console.error('GET /sundayschool/schedules', err);
          if (latestRequest.current === target) setError(errorMessageOf(err, '부서일정을 불러오지 못했습니다.'));
        })
        .finally(() => {
          if (latestRequest.current === target) setLoading(false);
        });
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    fetchMonth(month);
  }, [fetchMonth, month]);

  const days = useMemo(() => (data && data.month === month ? groupByDay(data) : []), [data, month]);
  const today = toYmd(new Date());
  const scheduleCount = data && data.month === month ? data.schedules.length : 0;
  const holidayCount = data && data.month === month ? data.holidays.length : 0;

  const openForm = (row?: ScheduleRow) => {
    if (row) {
      setEditingId(row.id);
      setForm({
        category: row.category,
        title: row.title,
        startDate: row.startDate,
        endDate: row.endDate,
        startTime: row.startTime,
        endTime: row.endTime,
        place: row.place,
        description: row.description ?? '',
      });
      setMultiDay(row.startDate !== row.endDate);
    } else {
      setEditingId(null);
      setForm(emptyForm(month === currentMonth() ? today : `${month}-01`));
      setMultiDay(false);
    }
    setFormError('');
    setFormOpen(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
  };

  const submitForm = () => {
    const payload = { ...identity(), ...form, endDate: multiDay ? form.endDate : form.startDate };
    if (!payload.title.trim() || !payload.startDate) return;
    setSaving(true);
    setFormError('');
    const request = editingId
      ? axios.put(`${MainURL}/sundayschool/schedules/${editingId}`, payload)
      : axios.post(`${MainURL}/sundayschool/schedules`, payload);

    request
      .then(() => {
        closeForm();
        // 저장한 일정이 있는 달로 이동해서 바로 확인할 수 있게 한다.
        const savedMonth = payload.startDate.slice(0, 7);
        if (savedMonth === month) fetchMonth(month);
        else setMonth(savedMonth);
      })
      .catch((err) => {
        console.error(editingId ? 'PUT /sundayschool/schedules/:id' : 'POST /sundayschool/schedules', err);
        setFormError(errorMessageOf(err, '저장하지 못했습니다.'));
      })
      .finally(() => setSaving(false));
  };

  const removeSchedule = (row: ScheduleRow) => {
    if (!window.confirm(`'${row.title}' 일정을 삭제할까요?`)) return;
    axios
      .delete(`${MainURL}/sundayschool/schedules/${row.id}`, { params: identity() })
      .then(() => fetchMonth(month))
      .catch((err) => {
        console.error('DELETE /sundayschool/schedules/:id', err);
        window.alert(errorMessageOf(err, '삭제하지 못했습니다.'));
      });
  };

  return (
    <SundaySchoolLayout footNote="부서일정 · 주일학교 앱 '부서일정' 탭에 표시됩니다">
      <div className="admin-schedule">
        <div className="page-head">
          <div>
            <h1>부서일정 🗓️</h1>
            <div className="sub">
              {department?.name ?? ''} 일정이 주일학교 앱 '부서일정' 탭에 날짜순 목록으로 표시돼요
            </div>
          </div>
          {canEdit && (
            <button type="button" className="btn btn-primary" onClick={() => openForm()}>
              + 새 일정 등록
            </button>
          )}
        </div>

        {formOpen && canEdit && (
          <div className="card card-pad mb16" ref={formRef}>
            <div className="card-title">
              {editingId ? '✏️ 일정 수정' : '➕ 새 일정 등록'}{' '}
              <button type="button" className="btn btn-ghost btn-sm" onClick={closeForm}>
                닫기
              </button>
            </div>
            <div className="grid2">
              <div className="field">
                <label>분류</label>
                <select
                  className="select"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value as ScheduleCategory })}
                >
                  {SCHEDULE_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>장소</label>
                <input
                  className="input"
                  value={form.place}
                  onChange={(e) => setForm({ ...form, place: e.target.value })}
                  placeholder="예: 교육관"
                />
              </div>
            </div>
            <div className="field">
              <label>제목</label>
              <input
                className="input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="예: 여름성경학교"
              />
            </div>
            <div className="grid2">
              <div className="field">
                <label>{multiDay ? '시작일' : '날짜'}</label>
                <input
                  className="input"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                />
              </div>
              {multiDay ? (
                <div className="field">
                  <label>종료일</label>
                  <input
                    className="input"
                    type="date"
                    value={form.endDate}
                    min={form.startDate}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  />
                </div>
              ) : (
                <div />
              )}
            </div>
            <label className="sched-check">
              <input
                type="checkbox"
                checked={multiDay}
                onChange={(e) => {
                  setMultiDay(e.target.checked);
                  if (e.target.checked && form.endDate < form.startDate) setForm({ ...form, endDate: form.startDate });
                }}
              />
              여러 날에 걸친 일정
            </label>
            <div className="grid2">
              <div className="field">
                <label>시작 시간 (선택)</label>
                <input
                  className="input"
                  type="time"
                  value={form.startTime}
                  onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                />
              </div>
              <div className="field">
                <label>종료 시간 (선택)</label>
                <input
                  className="input"
                  type="time"
                  value={form.endTime}
                  onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                />
              </div>
            </div>
            <div className="field">
              <label>설명</label>
              <textarea
                className="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            {formError && <div className="badge badge-danger mb16">{formError}</div>}
            <div className="between">
              <span className="muted sched-hint">시간을 비워 두면 '종일' 일정으로 표시됩니다.</span>
              <button
                type="button"
                className="btn btn-primary"
                onClick={submitForm}
                disabled={saving || !form.title.trim() || !form.startDate}
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        )}

        <div className="card card-pad">
          <div className="sched-monthbar">
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setMonth(shiftMonth(month, -1))}
              aria-label="이전 달"
            >
              ‹
            </button>
            <strong className="sched-month">{monthLabel(month)}</strong>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setMonth(shiftMonth(month, 1))}
              aria-label="다음 달"
            >
              ›
            </button>
            {month !== currentMonth() && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMonth(currentMonth())}>
                이번 달
              </button>
            )}
            <span className="muted sched-count">
              일정 {scheduleCount}건 · 공휴일 {holidayCount}일
            </span>
          </div>

          {loading && <div className="muted sched-state">불러오는 중입니다...</div>}
          {!loading && error && <div className="badge badge-danger">{error}</div>}
          {!loading && !error && days.length === 0 && (
            <div className="muted sched-state">{monthLabel(month)}에 등록된 일정이 없습니다.</div>
          )}
          {!loading &&
            !error &&
            days.map((day) => {
              const weekday = weekdayIndex(day.date);
              const tone = weekday === 0 || day.holidays.length > 0 ? ' is-red' : weekday === 6 ? ' is-blue' : '';
              return (
                <div key={day.date} className={`sched-day${day.date < today ? ' is-past' : ''}`}>
                  <div className={`sched-date${tone}`}>
                    <b>{Number(day.date.slice(8, 10))}</b>
                    <span>{WEEKDAYS[weekday]}</span>
                    {day.date === today && <em>오늘</em>}
                  </div>
                  <div className="sched-items">
                    {day.holidays.map((holiday) => (
                      <div key={`h-${holiday.id}`} className="sched-holiday">
                        {holiday.name} <span className="chip badge-danger">공휴일</span>
                      </div>
                    ))}
                    {day.schedules.map((row) => (
                      <div key={row.id} className="sched-item">
                        <div className="grow">
                          <span className={`badge ${CATEGORY_BADGE[row.category] ?? 'badge-gray'}`}>
                            {row.category}
                          </span>
                          <div className="sched-title">{row.title}</div>
                          <div className="muted sched-meta">
                            {[scheduleTimeText(row), row.place].filter(Boolean).join(' · ')}
                          </div>
                          {!!row.description && <div className="sched-desc">{row.description}</div>}
                        </div>
                        {canEdit && (
                          <div className="flex">
                            <button type="button" className="btn btn-outline btn-sm" onClick={() => openForm(row)}>
                              수정
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline btn-sm"
                              onClick={() => removeSchedule(row)}
                            >
                              삭제
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
        </div>

        {!canEdit && <div className="muted mt16">일정 등록·수정은 부서담당자 이상만 할 수 있어요.</div>}
      </div>
    </SundaySchoolLayout>
  );
}
